import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormikContext } from 'formik';
import { useRecoilValue } from 'recoil';
import { createCutsheetSteps } from './step-configuration';
import { WizardStep } from './wizard-step';
import {
  CutsheetWizardFormValues,
  StepConfig,
  WizardContext,
  WizardStepId,
} from './types';
import { WizardNavProvider } from './navigation-context';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Slide,
  Typography,
} from '@mui/material';
import { ProgressBar, useModal } from '@farmshare/ui-components';
import {
  ArrowBack,
  ArrowForward,
  Close,
  Save as SaveIcon,
} from '@mui/icons-material';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import {
  type Cutsheet,
  type CutsheetCreateOneMutationVariables,
  EnumCutsheetAnimalSpecies,
  EnumCutsheetInspectionLevels,
  EnumCutsheetType,
  type ProcessorCapability,
  type ProcessorSettings,
  useCutsheetCreateOneMutation,
  useCutsheetSoftDeleteMutation,
} from 'lib/graphql';
import { userState } from 'state';
import { useWizardConfig } from './context';
import type { CutsheetRequest } from 'pages/scheduling/_views/edit-cutsheets';
import { SchedulingCustomCutsheetModal } from 'pages/scheduling/_views/custom-cutsheet-modal';
import type {
  EditCutsheetFormData,
  Extras,
  Trim,
} from 'pages/processor/cutsheets/_views/form-sections';
import { filter, map, omit } from 'lodash';

interface CutsheetWizardDialogProps {
  onClose: () => void;
  open: boolean;
  settings?: ProcessorSettings;
  capabilities?: ProcessorCapability[];
  animalSpecies?: EnumCutsheetAnimalSpecies;
  inspectionLevel?: EnumCutsheetInspectionLevels;
  stepsOverride?: StepConfig<WizardContext>[];
  renderInline?: boolean;
  isPreviewMode?: boolean;
  onCutsheetCreated?: () => void;
  onSubmitWithCutsheet?: (cutsheet: Cutsheet, selectedHeads: string[]) => void;
  userCutsheetRequests?: CutsheetRequest[];
  onUseAdvanced?: () => void;
  cutsheetType?: 'vendor' | 'producer' | 'buyer' | 'user';
  userId?: string;
  buyerId?: string;
  producerId?: string;
  templateCutsheet?: Cutsheet;
}

export function CutsheetWizardDialog({
  onClose,
  open,
  settings = undefined,
  capabilities = undefined,
  animalSpecies = undefined,
  inspectionLevel = undefined,
  stepsOverride = undefined,
  renderInline = false,
  isPreviewMode = false,
  onCutsheetCreated = undefined,
  onSubmitWithCutsheet = undefined,
  userCutsheetRequests = undefined,
  onUseAdvanced = undefined,
  cutsheetType = undefined,
  userId = undefined,
  buyerId = undefined,
  producerId = undefined,
  templateCutsheet: _templateCutsheet = undefined,
}: CutsheetWizardDialogProps) {
  const [currentStep, setCurrentStep] = useState<WizardStepId>('intro');
  const isGoingForward = useRef(true);
  const [selectedCutsheets, setSelectedCutsheets] = useState<string[]>([]);
  const [hasSeenReview, setHasSeenReview] = useState(false);

  const cancel = useCallback(onClose, [onClose]);

  // Access formik values and user state
  const formikContext = useFormikContext<CutsheetWizardFormValues>();
  const { values } = formikContext;
  const user = useRecoilValue(userState);
  const {
    animalSpecies: configAnimalSpecies,
    inspectionLevel: configInspectionLevel,
    activeSpecification,
  } = useWizardConfig();
  const [createCutsheet, createOp] = useCutsheetCreateOneMutation();
  const [deleteCutsheet] = useCutsheetSoftDeleteMutation();
  const { save } = useModal();

  // Default "Use Advanced View" handler — opens the SchedulingCustomCutsheetModal
  // and routes the created cutsheet through the same onSubmitWithCutsheet /
  // onCutsheetCreated callbacks the wizard already exposes. Callers can still
  // provide their own onUseAdvanced to override this.
  const defaultHandleAdvancedView = useCallback(() => {
    if (isPreviewMode) return;
    save<EditCutsheetFormData>({
      type: 'save',
      title: 'Custom Cutsheet',
      icon: faPlus,
      fullscreen: true,
      isLoading: createOp.loading,
      initialValues: {
        name: '',
        cuts: [],
        extras: [],
        trim: [],
        selectedSpecifications: {},
        animalSpecies: animalSpecies as unknown as EnumCutsheetAnimalSpecies,
        blockedBy: [],
        inspectionLevel:
          inspectionLevel as unknown as EnumCutsheetInspectionLevels,
        canSelectMoreThanOneTrim: true,
        trimSelection: 'allowSelectionByWeight',
        cutsheetType: cutsheetType ?? (producerId ? 'producer' : 'vendor'),
        type:
          cutsheetType === 'vendor' || (!cutsheetType && !producerId)
            ? 'vendor'
            : 'user',
        userId: producerId ?? buyerId ?? userId,
      },
      body: (fP) => (
        <SchedulingCustomCutsheetModal
          fP={fP}
          capabilities={capabilities}
          animalSpecies={animalSpecies as any}
          settings={settings}
          currentUserId={user?._id}
          producerId={producerId}
          buyerId={buyerId}
        />
      ),
      onSubmit: async ({
        name,
        cuts,
        selectedSpecifications,
        activeSpecification: submittedSpec,
        extras,
        trim,
        notes,
        inspectionLevel: submittedInspectionLevel,
        type,
        userId: submittedUserId,
      }) => {
        const formattedExtras = animalSpecies
          ? (map(filter(extras, (extra) => extra?.isActive) || [], (extra) => ({
              ...omit(extra, ['__typename', 'disabled', 'description']),
              quantity: Number(extra.quantity),
            })) as Extras[])
          : [];

        const formattedTrim = animalSpecies
          ? (map(
              filter(trim, (trimItem) => trimItem?.isActive) || [],
              (trimItem) => ({
                ...omit(trimItem, ['__typename', 'disabled', 'description']),
                quantity: Number(trimItem.quantity),
              }),
            ) as Trim[])
          : [];

        const variables: CutsheetCreateOneMutationVariables = {
          record: {
            type:
              type === 'vendor'
                ? EnumCutsheetType.Vendor
                : EnumCutsheetType.User,
            animalSpecies:
              animalSpecies as unknown as EnumCutsheetAnimalSpecies,
            inspectionLevels: submittedInspectionLevel
              ? [
                  submittedInspectionLevel as unknown as EnumCutsheetInspectionLevels,
                ]
              : [],
            name,
            primalCuts: map(cuts, (c) => c.value),
            activeSpecification: submittedSpec,
            selectedSpecifications,
            extras: formattedExtras,
            trim: formattedTrim,
            notes,
            user: submittedUserId,
            createdBy: user?._id,
            updatedBy: user?._id,
            vendor: settings?.vendor?._id,
          },
        };

        const response = await createCutsheet({ variables });
        const created = response?.data?.cutsheetCreateOne?.record as
          | Cutsheet
          | undefined;
        if (!created) return;

        if (onSubmitWithCutsheet) {
          onSubmitWithCutsheet(created, []);
        } else if (onCutsheetCreated) {
          onCutsheetCreated();
        }
      },
    });
  }, [
    isPreviewMode,
    save,
    createOp.loading,
    animalSpecies,
    inspectionLevel,
    cutsheetType,
    producerId,
    buyerId,
    userId,
    capabilities,
    settings,
    user?._id,
    createCutsheet,
    onSubmitWithCutsheet,
    onCutsheetCreated,
  ]);

  const handleAdvancedView = onUseAdvanced ?? defaultHandleAdvancedView;

  // Build wizard context with smart fallback logic for settings-derived values
  const context: WizardContext = useMemo(() => {
    /**
     * Smart fallback logic for multi-variant processors:
     * When a processor has multiple animalSettings entries (e.g., beef-exempt, beef-state, pork-state),
     * we need to pick the correct one if explicit props aren't provided.
     *
     * Strategy: Find the FIRST ENABLED animalSettings entry and use its species/inspectionLevel
     * as fallback values. This handles cases where:
     * 1. The wizard is invoked without explicit animalSpecies/inspectionLevel props
     * 2. The processor has disabled certain variants (isEnabled: false)
     * 3. We need consistent defaults across dialog initialization
     */
    const firstEnabledAnimalSettings = settings?.animalSettings?.find(
      (as) => as?.isEnabled,
    );

    return {
      selectedCutsheets,
      setSelectedCutsheets,
      goNext: () => {}, // Populated by WizardNavProvider
      goBack: () => {}, // Populated by WizardNavProvider
      cancel,
      // Add current form values for validation access
      formValues: values,
      // Capability fallback: Use explicit prop OR derive from settings object
      capabilities:
        capabilities ||
        (settings as { capabilities?: ProcessorCapability[] })?.capabilities,
      settings,
      // Species fallback: Use explicit prop OR first enabled variant's species
      animalSpecies:
        animalSpecies ||
        (firstEnabledAnimalSettings?.species as unknown as EnumCutsheetAnimalSpecies),
      // Inspection level fallback: Use explicit prop OR first enabled variant's first inspection level
      inspectionLevel:
        inspectionLevel ||
        (firstEnabledAnimalSettings
          ?.inspectionLevels?.[0] as unknown as EnumCutsheetInspectionLevels),
    };
  }, [
    selectedCutsheets,
    settings,
    capabilities,
    animalSpecies,
    inspectionLevel,
    cancel,
    values,
  ]);

  const steps = useMemo(() => {
    const base = createCutsheetSteps(context);
    return stepsOverride?.length ? [...base, ...stepsOverride] : base;
  }, [context, stepsOverride]);

  const ids = steps.map((s) => s.id);
  const currentIndex = Math.max(0, ids.indexOf(currentStep));
  const totalSteps = steps.length;
  const currentConfig = steps[currentIndex];

  // Track when user reaches the review step
  useEffect(() => {
    if (currentStep === 'review') {
      setHasSeenReview(true);
    }
  }, [currentStep]);

  const doGoNext = useCallback(
    async (skipValidation = false) => {
      if (
        !skipValidation &&
        currentConfig?.canGoNext &&
        !(await currentConfig.canGoNext(context))
      )
        return;
      if (currentConfig?.onNext) await currentConfig.onNext(context);
      const next = steps[currentIndex + 1]?.id;
      if (next) {
        isGoingForward.current = true;
        setCurrentStep(next);
      } else onClose();
    },
    [currentConfig, context, steps, currentIndex, onClose],
  );

  const doGoBack = useCallback(async () => {
    if (currentConfig?.onBack) await currentConfig.onBack(context);
    const prev = steps[currentIndex - 1]?.id;
    if (prev) {
      isGoingForward.current = false;
      setCurrentStep(prev);
    } else onClose();
  }, [currentConfig, context, steps, currentIndex, onClose]);

  const handleCreateCutsheet = useCallback(
    async (shouldSubmit: boolean = false) => {
      // Guard: Prevent mutation execution in preview mode
      if (isPreviewMode) {
        return;
      }

      // Get fresh values at execution time to avoid stale closure issues
      const currentValues = formikContext.values;

      // Validate name is present
      if (!currentValues.name || currentValues.name.trim().length === 0) {
        return;
      }

      // Map selected specifications to their string value
      const selectedSpecifications = Object.fromEntries(
        Object.entries(currentValues.selectedSpecifications || {}).map(
          ([key, value]) => {
            const spec = activeSpecification?.specifications?.find(
              (spec) => spec?.name === key,
            );

            const valueToUse =
              spec?.options?.find((opt) => opt?._id === value)?.name ?? ';';

            return [key, valueToUse];
          },
        ),
      );

      // Determine cutsheet type and user ID based on cutsheetType selection
      let finalCutsheetType: EnumCutsheetType;
      let cutsheetUserId: string | undefined;

      // Auto-determine type based on props if not explicitly provided
      let typeToUse = cutsheetType || currentValues.cutsheetType;

      // If no type specified but we have buyerId or producerId, determine type
      if (!typeToUse) {
        if (producerId) {
          typeToUse = 'producer';
        } else if (buyerId) {
          typeToUse = 'buyer';
        }
      }

      switch (typeToUse) {
        case 'vendor':
          finalCutsheetType = EnumCutsheetType.Vendor;
          cutsheetUserId = undefined; // Vendor cutsheets don't have a user
          break;
        case 'producer':
          finalCutsheetType = EnumCutsheetType.User;
          // Use producerId prop if provided, otherwise use userId prop, otherwise fall back to current user
          cutsheetUserId = producerId || userId || user?._id;
          break;
        case 'buyer':
          finalCutsheetType = EnumCutsheetType.User;
          // Use buyerId prop if provided, otherwise use userId prop, otherwise fall back to current user
          cutsheetUserId = buyerId || userId || user?._id;
          break;
        case 'user':
        default:
          finalCutsheetType = user?._id
            ? EnumCutsheetType.User
            : EnumCutsheetType.Orphan;
          cutsheetUserId = user?._id ?? undefined;
          break;
      }

      // Create cutsheet mutation variables
      const variables: CutsheetCreateOneMutationVariables = {
        record: {
          type: finalCutsheetType,
          user: cutsheetUserId,
          animalSpecies: (configAnimalSpecies ||
            animalSpecies) as unknown as EnumCutsheetAnimalSpecies,
          inspectionLevels: [configInspectionLevel || inspectionLevel!],
          primalCuts: (currentValues.cuts ?? [])
            .map((c) => c?._id)
            .filter(Boolean),
          name: currentValues.name,
          selectedSpecifications,
          extras: currentValues.extras.map((e) =>
            omit(e, ['description', 'disabled']),
          ) as unknown as CutsheetCreateOneMutationVariables['record']['extras'],
          trim: currentValues.trim.map((t) =>
            omit(t, ['description', 'disabled']),
          ) as unknown as CutsheetCreateOneMutationVariables['record']['trim'],
          createdBy: user?._id,
          updatedBy: user?._id,
          activeSpecification: activeSpecification?._id,
          vendor: settings?.vendor?._id,
          notes: values.notes,
          pricePerPound: currentValues.pricePerPound,
        },
      };

      try {
        // If replacing original, delete it first
        if (currentValues.replaceOriginal && _templateCutsheet?._id) {
          await deleteCutsheet({
            variables: {
              cutsheetId: _templateCutsheet._id,
            },
            fetchPolicy: 'no-cache',
          });
        }

        const res = await createCutsheet({
          variables,
          // Force Apollo to bypass cache and fetch fresh data from server
          fetchPolicy: 'no-cache',
        });
        const created = res.data?.cutsheetCreateOne?.record;

        if (created) {
          // If onSubmitWithCutsheet is provided, use it (allows parent to handle selection)
          if (onSubmitWithCutsheet) {
            // Pass the created cutsheet and selected heads (if any from wizard)
            onSubmitWithCutsheet(
              created as Cutsheet,
              currentValues.selectedHeads || [],
            );
            // Close the wizard after successful submission
            onClose();
          } else {
            // Call the callback to refresh the cutsheet list
            if (onCutsheetCreated) {
              onCutsheetCreated();
            }
            // Just close the wizard for regular save
            onClose();
          }
        }
      } catch {
        // Error is silently handled - mutation errors are shown through Apollo
      }
    },
    [
      isPreviewMode,
      formikContext,
      activeSpecification,
      user,
      configAnimalSpecies,
      configInspectionLevel,
      animalSpecies,
      inspectionLevel,
      createCutsheet,
      onClose,
      onCutsheetCreated,
      onSubmitWithCutsheet,
      settings?.vendor?._id,
      values.notes,
      cutsheetType,
      userId,
      userCutsheetRequests,
      deleteCutsheet,
      _templateCutsheet,
    ],
  );

  // Rebind navigation into context so step actions can use it
  context.goNext = doGoNext;
  context.goBack = doGoBack;
  context.goTo = (id: WizardStepId) => setCurrentStep(id);

  const onSelectionChange = useCallback(
    async (specKey?: string, specValue?: string) => {
      // Auto-advance logic: Move to next step if there's only one option
      // OR if there's only one option available (regardless of step type)
      const shouldAutoAdvance = currentConfig?.autoAdvanceOnSelection;

      if (shouldAutoAdvance) {
        // Use requestAnimationFrame to ensure form state has updated after current render
        requestAnimationFrame(() => {
          // Much longer delay to allow multiple React render cycles
          setTimeout(() => {
            // Check if we can actually go next before auto-advancing
            if (
              currentConfig?.canGoNext &&
              !currentConfig?.id?.startsWith('spec:')
            ) {
              // For non-specification steps, validate before advancing
              try {
                // Get the absolute freshest form values directly from Formik context
                const absoluteLatestValues = formikContext.values;

                // If we have fresh spec data, use it to override stale form state
                let enhancedValues = absoluteLatestValues;
                if (specKey && specValue) {
                  enhancedValues = {
                    ...absoluteLatestValues,
                    selectedSpecifications: {
                      ...absoluteLatestValues.selectedSpecifications,
                      [specKey]: specValue,
                    },
                  };
                }

                const freshContext = {
                  ...context,
                  formValues: enhancedValues, // Use enhanced values with fresh spec data
                };
                const canProceed = currentConfig.canGoNext(freshContext);

                if (canProceed) {
                  doGoNext();
                } else {
                  // For auto-advance, if validation fails, try proceeding anyway
                  // since the user just made a selection
                  doGoNext();
                }
              } catch (error) {
                // If validation fails, still auto-advance since user made selection
                doGoNext();
              }
            } else {
              // For specification steps or steps without validation, always proceed
              doGoNext(true); // Skip validation for spec steps
            }
          }, 100);
        });
      }
    },
    [currentConfig, doGoNext, context, formikContext],
  );

  context.onSelectionChange = onSelectionChange;

  // Check if the current step can go next (for button state)
  const [canGoNextState, setCanGoNextState] = useState(true);

  // Update can go next state when current step changes
  useEffect(() => {
    const checkCanGoNext = async () => {
      if (!currentConfig?.canGoNext) {
        setCanGoNextState(true);
        return;
      }
      try {
        const result = await currentConfig.canGoNext(context);
        setCanGoNextState(result);
      } catch {
        setCanGoNextState(false);
      }
    };

    checkCanGoNext();
  }, [currentConfig, context]);

  // Keyboard navigation for power users
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle arrow keys if the wizard is open and no input is focused
      if (!open) return;

      const { activeElement } = document;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.getAttribute('contenteditable') === 'true');

      if (isInputFocused) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          // Only go back if we're not on the first step
          if (currentIndex > 0) {
            doGoBack();
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          // Only go next if the next button would be enabled
          if (canGoNextState && currentIndex < totalSteps - 1) {
            doGoNext();
          }
          break;
        default:
          // No action needed for other keys
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, currentIndex, totalSteps, canGoNextState, doGoBack, doGoNext]);

  const wizardContent = (
    <>
      <AppBar
        color="default"
        position="sticky"
        sx={{
          bgcolor: 'background.paper',
          boxShadow: 'none',
          color: 'text.primary',
          px: 2,
          py: 1,
        }}
      >
        <Box
          sx={{ maxWidth: '57rem', mx: 'auto', width: '100%', px: 2, py: 2 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography
              variant="h4"
              color="primary.main"
              sx={{ fontWeight: 700 }}
            >
              Cutsheet Wizard
            </Typography>
            <IconButton aria-label="Close" onClick={onClose} color="primary">
              <Close />
            </IconButton>
          </Box>
          <Box sx={{ mt: 1 }}>
            <ProgressBar current={currentIndex + 1} total={totalSteps} />
          </Box>
        </Box>
      </AppBar>
      <DialogContent
        sx={{ bgcolor: 'background.default', p: { xs: 2, sm: 3 } }}
      >
        <Box sx={{ mt: 2, maxWidth: '42rem', mx: 'auto' }}>
          {currentConfig && (
            <WizardNavProvider
              value={{
                goNext: doGoNext,
                goBack: doGoBack,
                goTo: (id) => setCurrentStep(id),
                cancel,
              }}
            >
              <Slide
                in
                key={currentStep}
                direction={isGoingForward.current ? 'left' : 'right'}
                timeout={{ enter: 300, exit: 200 }}
              >
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    mx: 'auto',
                    p: { xs: 2, sm: 3 },
                  }}
                >
                  <WizardStep config={currentConfig} context={context} />
                </Card>
              </Slide>
            </WizardNavProvider>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
          p: 2,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Example primary CTA per step */}
        {currentConfig?.id === 'intro' && (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', gap: 2, flex: 1 }}
          >
            <Button
              variant="contained"
              onClick={() => doGoNext()}
              sx={{ fontSize: '1.15rem', gap: 1 }}
              endIcon={<ArrowForward />}
            >
              Let&apos;s Get Started!
            </Button>
            {!isPreviewMode && (
              <Button
                variant="outlined"
                onClick={() => {
                  onClose();
                  handleAdvancedView();
                }}
                sx={{ fontSize: '1.15rem', gap: 1 }}
              >
                Use Advanced View
              </Button>
            )}
          </Box>
        )}
        {currentConfig?.id === 'naming' && (
          <>
            {isPreviewMode && (
              <Alert severity="info" icon={false} sx={{ mt: 2 }}>
                This is a preview of the cutsheet wizard your customers will
                see. The Save buttons are disabled in preview mode.
              </Alert>
            )}
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'space-between',
                flex: 1,
              }}
            >
              <Button
                variant="outlined"
                onClick={doGoBack}
                sx={{
                  fontSize: '1rem',
                  width: '120px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                }}
              >
                <ArrowBack sx={{ color: 'primary.main' }} />
                <Box component="span">Previous</Box>
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    // Get fresh values at click time to avoid stale closure issues
                    const currentValues = formikContext.values;
                    const hasSelectedHeads =
                      currentValues.selectedHeads &&
                      currentValues.selectedHeads.length > 0;

                    // Pass true only if heads are selected, false for just saving
                    handleCreateCutsheet(hasSelectedHeads);
                  }}
                  disabled={
                    isPreviewMode ||
                    !values.name ||
                    values.name.trim().length === 0 ||
                    (!cutsheetType && !values.cutsheetType) ||
                    createOp.loading
                  }
                  sx={{
                    fontSize: '1rem',
                    px: 3,
                  }}
                  startIcon={<SaveIcon />}
                >
                  {values.selectedHeads && values.selectedHeads.length > 0
                    ? 'Save and Submit'
                    : 'Save'}
                </Button>
              </Box>
            </Box>
          </>
        )}
        {currentConfig?.id !== 'intro' && currentConfig?.id !== 'naming' && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flex: 1,
            }}
          >
            <Button
              variant="outlined"
              onClick={doGoBack}
              sx={{
                fontSize: '1rem',
                width: '120px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2,
              }}
            >
              <ArrowBack sx={{ color: 'primary.main' }} />
              <Box component="span">Previous</Box>
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {hasSeenReview &&
                currentConfig?.id !== 'review' &&
                currentConfig?.id !== 'naming' && (
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentStep('review')}
                    sx={{
                      fontSize: '1rem',
                      px: 3,
                    }}
                  >
                    Return To Review
                  </Button>
                )}
              <Button
                variant="contained"
                onClick={() => doGoNext()}
                disabled={!canGoNextState}
                sx={{
                  fontSize: '1rem',
                  width: '120px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                }}
              >
                <Box component="span">
                  {currentIndex === totalSteps - 1 ? 'Complete' : 'Next'}
                </Box>
                <ArrowForward sx={{ color: 'background.paper' }} />
              </Button>
            </Box>
          </Box>
        )}
      </DialogActions>
    </>
  );

  if (renderInline) {
    return open ? wizardContent : null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      {wizardContent}
    </Dialog>
  );
}
